import { ReactNode, useEffect } from 'react'
import { X } from 'lucide-react'
import { clsx } from 'clsx'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  children: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  actions?: ReactNode
}

// Touch-optimized modal for Galaxy Tab Active5
// Nordic Industrial palette
const Modal = ({ isOpen, onClose, title, children, size = 'xl', actions }: ModalProps) => {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  if (!isOpen) return null

  const sizes = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl',
    full: 'max-w-full mx-4',
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className={clsx(
        'relative bg-white rounded-2xl shadow-2xl w-full max-h-[95vh] sm:max-h-[90vh] flex flex-col border border-stone-200',
        sizes[size]
      )}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b-2 border-stone-200 bg-stone-50 rounded-t-2xl">
          <h2 className="text-xl sm:text-3xl font-semibold text-[#1E3A5F] truncate pr-2">{title}</h2>
          <div className="flex items-center gap-2 flex-shrink-0">
            {actions && <div className="flex items-center gap-2 mr-2">{actions}</div>}
            <button
              onClick={onClose}
              className="p-2 hover:bg-stone-200 rounded-lg transition-colors min-h-[48px] min-w-[48px] flex items-center justify-center"
            >
              <X size={28} className="sm:w-8 sm:h-8 text-stone-500" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {children}
        </div>
      </div>
    </div>
  )
}

export default Modal

